import DownloadIcon from "@mui/icons-material/Download";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import { Alert, Dialog } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useNavigation } from "@/providers";
import { useFileTransfer } from "@/providers/FileTransfer";
import { AddToCollection } from "@/shared/components/collections";
import { IngestionLabelBadge } from "@/shared/components/files/IngestionLabelBadge";
import {
  ResourceView,
  type SelectionOption,
} from "@/shared/components/ResourceView";
import { RobotoButton } from "@/shared/components/RobotoButton";
import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import { useCreateExportJobMutation } from "@/shared/domain/exportJobs/hooks";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { ingestionLabel, IngestionStatus } from "@/shared/domain/files";
import { type IngestionRun } from "@/shared/domain/ingestionRules";
import {
  ingestionRulesQueryKeys,
  useLatestIngestionRuns,
} from "@/shared/domain/ingestionRules/hooks";
import {
  type SessionFile,
  type SessionIngestionStatus,
} from "@/shared/domain/sessions";
import {
  sessionQueryKeys,
  useSessionFiles,
  useSessionIngestionStatus,
} from "@/shared/domain/sessions/hooks";
import {
  buildColumnsForPreferences,
  cellStyles,
  colId,
  ColumnVisibility,
  createColumnHelper,
  DataTable,
  deriveLoadingState,
  EmptyCell,
  renderDashOr,
  useTable,
  type ColDef,
  type ColumnPickerOption,
  type ICellRendererParams,
  type ValueFormatterParams,
  type ValueGetterParams,
} from "@/shared/foundation/primitives/Table";
import { TagGroup } from "@/shared/foundation/primitives/Tag";
import { usePopupState } from "@/shared/hooks/usePopupState";
import { userFacingErrorMessage } from "@/shared/http";
import { Goto } from "@/shared/navigation/Goto";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { formatByteSize } from "@/shared/text";
import { formatNanoTimestamp } from "@/shared/time";

import styles from "./SourcesTab.module.css";
import { useSessionOutletContext } from "./useSessionOutletContext";

const columnHelper = createColumnHelper<SessionFile>();

function formatRangeBound(
  params: ValueFormatterParams<SessionFile, bigint | null>,
): string {
  // An absent bound means the session covers the file's whole time window;
  // render a dash so the column reads as "open" rather than missing data. AG
  // Grid widens `value` to `TValue | null | undefined`, so collapse both.
  if (params.value === null || params.value === undefined) {
    return "—";
  }
  return formatNanoTimestamp(params.value);
}

const INGESTION_COLUMN_ID = "ingestion_status";

// Mirrors the /files page's column set where the fields overlap; the
// range columns are session-specific. All values arrive on the row
// itself — the listing endpoint is a view model joined server-side.
const COLUMNS: ColDef<SessionFile>[] = [
  columnHelper.accessor("file_id", {
    headerName: "File ID",
    flex: 2,
    cellRenderer: (p: ICellRendererParams<SessionFile, string>) => {
      const id = p.value;
      if (!id) {
        return null;
      }
      return (
        <RobotoLinkHoverUnderline
          to={`${Goto.route.Files}/${id}`}
          className={cellStyles.idCell}
        >
          {id}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 2,
    cellRenderer: (p: ICellRendererParams<SessionFile, string | null>) => {
      const row = p.data;
      if (!row) {
        return null;
      }
      // The id is the label of last resort (a response predating the view
      // model); the link target is valid either way.
      return (
        <RobotoLinkHoverUnderline
          to={`${Goto.route.Files}/${row.file_id}`}
          className={cellStyles.primaryContent}
          title={row.relative_path ?? undefined}
        >
          {p.value ?? row.file_id}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  // `withIngestion` fills in its value and cell.
  {
    colId: INGESTION_COLUMN_ID,
    headerName: "Ingestion",
    minWidth: 170,
    sortable: false,
  },
  columnHelper.timestamp("created", {
    headerName: "Created",
    width: 160,
    variant: "relative",
  }),
  columnHelper.accessor("dataset_id", {
    headerName: "Dataset ID",
    width: 200,
    cellClass: cellStyles.monoCell,
    cellRenderer: (p: ICellRendererParams<SessionFile, string | null>) => {
      // Session files live in the filesystem world, where a file's
      // association is always its dataset; the wire row carries no
      // association_type to gate on, so the link is unconditional.
      const datasetId = p.value;
      if (!datasetId) {
        return <EmptyCell />;
      }
      return (
        <RobotoLinkHoverUnderline
          to={`${Goto.route.Datasets}/${datasetId}`}
          className={cellStyles.idCell}
        >
          {datasetId}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.accessor("tags", {
    headerName: "Tags",
    flex: 2,
    cellClass: cellStyles.tagsCell,
    valueFormatter: (p) => (p.value ?? []).join(", "),
    cellRenderer: renderDashOr(
      (p: ICellRendererParams<SessionFile, string[]>) => (
        <TagGroup tags={p.value ?? []} maxDisplayCount={3} />
      ),
    ),
  }),
  // A user declares the window in the file's own timestamps, and these cells
  // report it in wall clock; the two differ by `unix_epoch_offset_ns`, so the
  // headers name which of the two bases the numbers are in.
  columnHelper.accessor("min_wall_clock_timestamp_ns", {
    headerName: "Range start (wall clock)",
    flex: 1,
    cellClass: cellStyles.monoCell,
    valueFormatter: formatRangeBound,
  }),
  columnHelper.accessor("max_wall_clock_timestamp_ns", {
    headerName: "Range end (wall clock)",
    flex: 1,
    cellClass: cellStyles.monoCell,
    valueFormatter: formatRangeBound,
  }),
  // The remaining columns are omitted from DEFAULT_VISIBLE_IDS below, so
  // they start hidden and are reachable through the column picker.
  columnHelper.accessor("unix_epoch_offset_ns", {
    headerName: "Time 0 (wall clock)",
    width: 200,
    cellClass: cellStyles.monoCell,
    // The instant the file's own time 0 sits at: subtracting it from either
    // column above reads that bound back in the file's own timestamps. Blank
    // when the server reports no single offset for this file's place in the
    // session.
    valueFormatter: (p) => formatNanoTimestamp(p.value),
  }),
  {
    colId: "source",
    field: "origination",
    headerName: "Source",
    width: 200,
    cellClass: cellStyles.monoCell,
    cellRenderer: (p: ICellRendererParams<SessionFile, string | null>) => {
      // Origination is free text written by uploader clients ("invocation:
      // iv_…", "roboto client vX"), not a parseable id, so it renders as
      // plain text with no link affordance.
      const origination = p.value ?? "";
      if (!origination) {
        return <EmptyCell />;
      }
      return origination;
    },
  },
  columnHelper.accessor("size", {
    headerName: "Size",
    width: 100,
    valueFormatter: (p) =>
      p.value === null || p.value === undefined ? "" : formatByteSize(p.value),
  }),
  columnHelper.timestamp("modified", {
    headerName: "Modified",
    width: 160,
    variant: "relative",
  }),
  columnHelper.accessor("relative_path", {
    headerName: "Path",
    flex: 2,
    cellClass: "truncate",
  }),
];

const DEFAULT_VISIBLE_IDS = [
  "file_id",
  "name",
  "ingestion_status",
  "created",
  "dataset_id",
  "tags",
  "min_wall_clock_timestamp_ns",
  "max_wall_clock_timestamp_ns",
];

/** The fields the ingestion label reads, or null for a row from a service that predates them. */
function labelledFile(file: SessionFile | undefined) {
  if (
    file === undefined ||
    file.ingestion_status === undefined ||
    file.ingestion_status === null
  ) {
    return null;
  }
  return {
    file_id: file.file_id,
    created: file.created ?? undefined,
    ingestion_status: file.ingestion_status,
    ingestable: file.ingestable ?? false,
    relative_path: file.relative_path ?? file.name ?? "",
  };
}

/** The most files `useLatestIngestionRuns` asks about at once. */
const MAX_RUN_LOOKUPS = 200;

/** What the Ingestion column knows beyond each row: runs and skipped files. */
interface SourcesIngestion {
  /** Undefined when not known, so the label looks it up itself. */
  latestRun: (fileId: string) => IngestionRun | null | undefined;
  waitSkipped: (
    fileId: string,
  ) => { skippedAt: string | null; skippedBy: string | null } | undefined;
}

/**
 * The columns with the Ingestion column filled in from the page's runs and
 * skips: its cell is the label and popover the dataset and files tables show,
 * and its value is that label.
 */
function withIngestion(
  columns: ColDef<SessionFile>[],
  ingestion: SourcesIngestion,
): ColDef<SessionFile>[] {
  return columns.map((column) =>
    colId(column) === INGESTION_COLUMN_ID
      ? {
          ...column,
          valueGetter: (p: ValueGetterParams<SessionFile>) => {
            const file = labelledFile(p.data);
            return file === null
              ? ""
              : ingestionLabel(file, {
                  latestRun: ingestion.latestRun(file.file_id),
                  waitSkipped:
                    ingestion.waitSkipped(file.file_id) !== undefined,
                });
          },
          cellRenderer: (p: ICellRendererParams<SessionFile>) => (
            <SessionFileIngestionLabel file={p.data} ingestion={ingestion} />
          ),
        }
      : column,
  );
}

/**
 * The latest ingestion runs of the page's files that are waiting on one, and
 * the files the session was told not to wait for.
 */
function useSourcesIngestion(
  rows: SessionFile[] | undefined,
  status: SessionIngestionStatus | undefined,
  orgId: string,
): SourcesIngestion {
  const candidates = React.useMemo(
    () =>
      (rows ?? [])
        .filter(
          (file) =>
            file.ingestable === true &&
            file.ingestion_status !== IngestionStatus.Ingested,
        )
        .map((file) => file.file_id)
        .slice(0, MAX_RUN_LOOKUPS),
    [rows],
  );
  const runs = useLatestIngestionRuns(candidates, { orgId });
  const runsData = runs.data;

  return React.useMemo(() => {
    const looked = new Set(candidates);
    const pendingRuns = new Map(
      (status?.pending_files ?? []).map((file) => [
        file.file_id,
        file.last_run,
      ]),
    );
    const skipped = new Map(
      (status?.skipped_files ?? []).map((file) => [file.file_id, file]),
    );
    return {
      latestRun: (fileId) =>
        runsData !== undefined && looked.has(fileId)
          ? runsData[fileId] ?? null
          : pendingRuns.get(fileId),
      waitSkipped: (fileId) => {
        const file = skipped.get(fileId);
        return file === undefined
          ? undefined
          : { skippedAt: file.skipped_at, skippedBy: file.skipped_by };
      },
    };
  }, [candidates, runsData, status]);
}

/**
 * Refetches the listed files and their runs after the session's ingestion
 * status changes, so the column's labels match the header's badge.
 */
function useRefetchOnIngestionChange(
  sessionId: string,
  orgId: string,
  status: SessionIngestionStatus | undefined,
) {
  const queryClient = useQueryClient();
  const previous = React.useRef(status);
  React.useEffect(() => {
    // Unchanged data keeps its identity across refetches.
    if (previous.current !== undefined && previous.current !== status) {
      void queryClient.invalidateQueries({
        queryKey: sessionQueryKeys.files(sessionId),
      });
      void queryClient.invalidateQueries({
        queryKey: [...ingestionRulesQueryKeys.all(orgId), "latestRuns"],
      });
    }
    previous.current = status;
  }, [queryClient, sessionId, orgId, status]);
}

/** A session file's ingestion label; the file is in the session's org. */
function SessionFileIngestionLabel({
  file,
  ingestion,
}: {
  file: SessionFile | undefined;
  ingestion: SourcesIngestion;
}) {
  const { session } = useSessionOutletContext();
  const labelled = labelledFile(file);
  if (file === undefined || labelled === null) {
    return <EmptyCell />;
  }
  return (
    <IngestionLabelBadge
      file={{ ...labelled, org_id: session.org_id }}
      latestRun={ingestion.latestRun(file.file_id)}
      waitSkipped={ingestion.waitSkipped(file.file_id)}
    />
  );
}

const AVAILABLE_COLUMNS: ColumnPickerOption[] = COLUMNS.map((c) => ({
  id: colId(c),
  label: c.headerName ?? colId(c),
}));

/** `AVAILABLE_COLUMNS` while `release-ingestion-labels` is off. */
const AVAILABLE_COLUMNS_WITHOUT_INGESTION = AVAILABLE_COLUMNS.filter(
  (option) => option.id !== INGESTION_COLUMN_ID,
);

const PREFERENCE_DRIVEN_COLUMNS = buildColumnsForPreferences<SessionFile>({
  staticColumns: COLUMNS,
  dynamicColumns: [],
  preferenceOrder: DEFAULT_VISIBLE_IDS,
});

/**
 * Sources tab of the session-detail page. Lists the files this session
 * holds, paged through the table footer, with no filters or sort. Each row
 * is a server-joined view of the file's place in the session and its
 * display fields, and rows are selectable for bulk actions (visualize,
 * download, add to collection).
 */
export function SourcesTab() {
  const { session } = useSessionOutletContext();
  const sessionId = session.session_id;
  const { goto } = useNavigation();
  const { mutateAsync: createExportJob } = useCreateExportJobMutation();
  const { beginExportJob } = useFileTransfer();
  const addToCollectionPopup = usePopupState<{
    ids: Set<string>;
    clearSelection: () => void;
  }>();
  // `usePopupState` rebuilds its container object every render; the stable
  // `open` callback is all the actions need, and depending on it keeps the
  // `selectionOptions` memo effective.
  const openAddToCollectionPopup = addToCollectionPopup.open;
  const [bulkActionError, setBulkActionError] =
    React.useState<React.ReactNode>(null);

  const table = useTable<SessionFile>({
    getRowId: (row) => row.file_id,
    defaultVisibleColumnIds: DEFAULT_VISIBLE_IDS,
  });

  // Each table page is one server page (`limit` = the footer's page
  // size), chained by token: page N's request carries the token page N-1
  // returned. The footer only steps one page at a time, so a page's
  // token is always recorded before the page is reachable.
  const [pageTokens, setPageTokens] = React.useState<(string | undefined)[]>(
    [],
  );
  const query = useSessionFiles({
    sessionId,
    pageToken: pageTokens[table.currentPage],
    limit: table.pageSize,
  });

  const { currentPage, pageSize } = table;
  const nextToken = query.data?.next_token;

  // Without `release-ingestion-labels` there is no Ingestion column, so
  // nothing reads the session's ingestion status or its files' runs.
  const ingestionLabelsShown = useCheckFeatureFlag("release-ingestion-labels");
  const ingestionStatus = useSessionIngestionStatus({
    session,
    enabled: ingestionLabelsShown,
  });
  useRefetchOnIngestionChange(sessionId, session.org_id, ingestionStatus.data);
  const sourcesIngestion = useSourcesIngestion(
    ingestionLabelsShown ? query.data?.items : undefined,
    ingestionStatus.data,
    session.org_id,
  );
  const columns = React.useMemo(
    () =>
      ingestionLabelsShown
        ? withIngestion(PREFERENCE_DRIVEN_COLUMNS, sourcesIngestion)
        : PREFERENCE_DRIVEN_COLUMNS.filter(
            (column) => colId(column) !== INGESTION_COLUMN_ID,
          ),
    [ingestionLabelsShown, sourcesIngestion],
  );

  // A different page size re-chunks the listing — and another session is
  // a different listing — so recorded tokens no longer point at page
  // boundaries; `useTable` resets `currentPage` to 0 on the same pageSize
  // transition. Declared before `recordNextPageToken`: when the page size
  // toggles back to a value whose first page is still cached, both
  // effects fire on one commit, and the wipe must run first — a
  // recorded-then-wiped token would never be re-recorded, because
  // `recordNextPageToken`'s deps don't change on the following commit.
  React.useEffect(
    function resetTokenChain() {
      setPageTokens([]);
    },
    [pageSize, sessionId],
  );

  React.useEffect(
    function recordNextPageToken() {
      if (!nextToken) {
        return;
      }
      setPageTokens((prev) => {
        if (prev[currentPage + 1] === nextToken) {
          return prev;
        }
        const next = [...prev];
        next[currentPage + 1] = nextToken;
        return next;
      });
    },
    [nextToken, currentPage],
  );

  const selectionOptions = React.useMemo<SelectionOption<SessionFile>[]>(
    () => [
      {
        name: "Visualize",
        icon: <QueryStatsIcon fontSize="small" />,
        action: ({ selected }) => {
          goto.visualize({
            dataSource: { files: selected.map((file) => file.file_id) },
          });
        },
      },
      {
        name: "Download",
        icon: <DownloadIcon fontSize="small" />,
        action: async ({ selected, clearSelection }) => {
          setBulkActionError(null);
          const label =
            selected.length === 1
              ? selected[0].name ?? selected[0].file_id
              : `${selected.length} files`;
          try {
            const result = await createExportJob({
              nodeIds: selected.map((file) => file.file_id),
            });
            beginExportJob(result.export_job_id, label);
            clearSelection();
          } catch (error) {
            ErrorMonitoringService.captureError(error);
            setBulkActionError(
              userFacingErrorMessage(
                error,
                "Failed to start the download. Please try again.",
              ),
            );
          }
        },
      },
      {
        name: "Add to Collection",
        icon: <PlaylistAddIcon fontSize="small" />,
        action: ({ selected, clearSelection }) => {
          openAddToCollectionPopup({
            metadata: {
              ids: new Set(selected.map((file) => file.file_id)),
              clearSelection,
            },
          });
        },
      },
    ],
    [openAddToCollectionPopup, beginExportJob, createExportJob, goto],
  );

  if (query.isError) {
    return (
      <Alert severity="error">
        {userFacingErrorMessage(
          query.error,
          <>
            Failed to load files. Please contact{" "}
            <a href="mailto:support@roboto.ai">support@roboto.ai</a> if this
            problem persists.
          </>,
        )}
      </Alert>
    );
  }

  return (
    <>
      <ResourceView.Root>
        <BulkActionErrorAlert
          message={bulkActionError}
          onDismiss={() => setBulkActionError(null)}
        />
        <ResourceView.Actions>
          <ColumnVisibility
            options={
              ingestionLabelsShown
                ? AVAILABLE_COLUMNS
                : AVAILABLE_COLUMNS_WITHOUT_INGESTION
            }
            visibleColumnIds={table.visibleColumnIds}
            setColumnVisibility={table.setColumnVisibility}
          />
          <ResourceView.Selection<SessionFile>
            selection={table.selection}
            clearSelection={table.clearSelection}
            options={selectionOptions}
          />
          <div className={styles.trailingActions}>
            <RobotoButton
              variant="outlined"
              size="small"
              startIcon={<QueryStatsIcon />}
              eventName="VisualizeSessionClicked"
              eventProperties={{ sessionId }}
              className={styles.visualizeAllButton}
              // Sends the session itself, so the visualizer opens every file
              // in it, including those on pages this table has not fetched:
              // the route lists the session's files itself. Nothing is
              // selected to do this, which is why it sits here rather than in
              // the selection menu beside the actions that read the selection.
              onClick={() => {
                goto.visualize({ dataSource: { sessions: [sessionId] } });
              }}
            >
              Visualize all
            </RobotoButton>
          </div>
        </ResourceView.Actions>
        <DataTable<SessionFile>
          table={table}
          columns={columns}
          data={query.data?.items ?? []}
          hasNextPage={Boolean(query.data?.next_token)}
          loading={deriveLoadingState(query)}
          noRowsContent="No files in this session yet"
          selectionEnabled
        />
      </ResourceView.Root>
      <Dialog
        open={addToCollectionPopup.isOpen}
        onClose={addToCollectionPopup.close}
      >
        <AddToCollection
          selected={{
            type: "File",
            items: addToCollectionPopup.metadata?.ids ?? new Set(),
          }}
          onCancel={addToCollectionPopup.close}
          onSuccess={() => addToCollectionPopup.metadata?.clearSelection()}
        />
      </Dialog>
    </>
  );
}

function BulkActionErrorAlert({
  message,
  onDismiss,
}: {
  message: React.ReactNode;
  onDismiss: () => void;
}) {
  if (!message) {
    return null;
  }
  return (
    <Alert severity="error" onClose={onDismiss}>
      {message}
    </Alert>
  );
}
